import { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthUser, LoginRequest, Role } from 'shared';
import { api, UnauthorizedError } from './api';

/** Nhãn tiếng Việt của 4 vai trò PRS §2.
 *
 * Đặt ở FE, không ở `packages/shared`: shared biên dịch ra CommonJS nên FE chỉ
 * import được KIỂU từ đó, giá trị lúc chạy sẽ không phân giải được (xem
 * `flow-steps.ts`).
 */
export const ROLE_LABEL: Record<Role, string> = {
  MARKETING_EXECUTIVE: 'Marketing Executive',
  CONTENT_EDITOR: 'Content Editor',
  WEB_ADMINISTRATOR: 'Web Administrator',
  PRODUCT_MARKETING_LEAD: 'Product Marketing Lead',
};

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({ user: null, loading: true });

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * Trạng thái đăng nhập lấy từ `GET /auth/me`.
 *
 * 401 KHÔNG phải lỗi cần thử lại — đó là câu trả lời "chưa đăng nhập". Nên tắt
 * retry cho riêng trường hợp này, nếu không màn đăng nhập sẽ hiện chậm vài giây.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: (_count, error) => !(error instanceof UnauthorizedError),
    staleTime: 5 * 60 * 1000,
  });

  // useMemo để giá trị context không phải object mới ở mỗi lần render — nếu không,
  // mọi component gọi `useAuth()` sẽ render lại theo mỗi render của provider.
  const value = useMemo<AuthState>(() => ({ user: data ?? null, loading: isLoading }), [
    data,
    isLoading,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => api.login(body),
    onSuccess: (user) => qc.setQueryData(['me'], user),
  });
}

/**
 * Đăng xuất rồi TẢI LẠI TRANG hẳn về trang chủ.
 *
 * Vì sao không dọn cache trong bộ nhớ: đã thử hai lần và cả hai đều sai.
 *  - Chỉ `queryClient.clear()`: xoá cache nhưng không thông báo cho observer đang
 *    mounted, nên `AuthProvider` giữ nguyên người dùng cũ.
 *  - Thêm `setQueryData(['me'], null)` sau `clear()`: vẫn sai, vì `clear()` đã huỷ
 *    chính query mà observer đang theo dõi, nên lệnh sau tạo một bản ghi MỚI mà
 *    observer cũ không gắn vào. Kết quả: màn hình đứng nguyên, còn các truy vấn khác
 *    thì báo 401 "Chưa đăng nhập" — trạng thái tệ nhất, người dùng không hiểu gì.
 *
 * Tải lại trang xoá sạch toàn bộ state trong bộ nhớ ở một bước, không phụ thuộc vào
 * chi tiết hoạt động của thư viện cache. Nó cũng đạt luôn hai việc mà `clear()` vốn
 * nhằm tới: không còn dữ liệu của người vừa đăng xuất, và đường dẫn về trang chủ.
 * Đăng xuất là lúc duy nhất tải lại cả trang không gây bất tiện gì.
 */
export function useLogout() {
  return useMutation({
    mutationFn: api.logout,
    // `onSettled` chứ không phải `onSuccess`: nếu lệnh gọi thất bại (mất mạng, server
    // chết) thì `onSuccess` không chạy và người dùng lại thấy "bấm mà không có gì xảy
    // ra". Tải lại trang trong mọi trường hợp thì màn hình luôn phản ánh trạng thái
    // THẬT — cookie đã xoá thì ra màn đăng nhập, chưa xoá được thì vẫn thấy đang đăng
    // nhập và bấm lại được, chứ không đứng im không rõ chuyện gì.
    onSettled: () => {
      window.location.assign('/');
    },
  });
}
